// ! This is a web-only component.
// ! Do not use this in native apps.

import React, { useCallback } from "react";
import { Pressable, TextInput } from "react-native";
import { XIcon } from "lucide-react-native";
import { usePlacesWidget } from "react-google-autocomplete";
import { FieldValues } from "react-hook-form";

import { AddressComponentType } from "@/domain/householdMember";
import { usePlatformOS } from "@/hooks/usePlatformOS";
import { useTranslator } from "@/hooks/useTranslate";
import { envVariables } from "@/utils/envVariables";

import { VnBox } from "./VnBox";
import { VnIcon } from "./VnIcon";
import { VnTextInput, VnTextInputPropsType } from "./VnTextInput/VnTextInput";

type PlaceType = {
  formatted_address?: string;
  name?: string;
  address_components?: AddressComponentType[];
  [key: string]: unknown;
};

type VnAddressInputPropsType<T extends FieldValues> = {
  onPlaceSelected: (
    addressComponents: AddressComponentType[],
    formattedAddress: string,
  ) => void;
  isAddressSelected?: boolean;
  onClear: () => void;
} & VnTextInputPropsType<T>;

const setRef = (
  ref: React.Ref<TextInput> | undefined,
  node: TextInput | null,
) => {
  if (!ref) return;

  if (typeof ref === "function") {
    ref(node);
  } else {
    (ref as { current: TextInput | null }).current = node;
  }
};

const VnAddressInput = <T extends FieldValues>({
  onPlaceSelected,
  isAddressSelected,
  onClear,
  ref: externalRef,
  ...props
}: VnAddressInputPropsType<T>) => {
  const { isWeb } = usePlatformOS();
  const { t } = useTranslator();

  const { ref: placesRef } = usePlacesWidget({
    apiKey: envVariables.EXPO_PUBLIC_PLACES_API_KEY,
    options: {
      types: ["address"],
      componentRestrictions: { country: "US" },
    },
    onPlaceSelected: (place: PlaceType) => {
      const address = place?.formatted_address ?? place?.name ?? "";
      const addressComponents = place?.address_components;
      if (address && addressComponents) {
        onPlaceSelected(addressComponents, address);
      }
    },
  });

  const mergedRef = useCallback(
    (node: TextInput | null) => {
      setRef(placesRef, node);
      setRef(externalRef, node);
    },
    [placesRef, externalRef],
  );

  if (!isWeb) return null;
  return (
    <VnTextInput
      ref={mergedRef}
      isDisabled={isAddressSelected}
      shouldApplyDisabledLabelStyle={false}
      rightElement={
        isAddressSelected ? (
          <Pressable
            onPress={onClear}
            accessibilityRole="button"
            accessibilityLabel={t("common:clearAddress")}>
            <VnBox className="bg-sidebar h-6 w-6 items-center justify-center rounded-full">
              <VnIcon as={XIcon} size={14} className="text-background" />
            </VnBox>
          </Pressable>
        ) : undefined
      }
      {...props}
    />
  );
};

export default VnAddressInput;
